import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  InputAdornment,
  Autocomplete,
  Checkbox,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { inventorySyncAdminService } from '../services'
import { formatOrganizationLabel } from '../utils/organizationLabel'
import { useOrganizationMultiSearch } from '../hooks/useOrganizationMultiSearch'

const SEARCH_DEBOUNCE_MS = 450

const STATUS_LABELS = {
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  reverted: 'Reverted',
}

const getStatusChipColor = (status) => {
  switch (status) {
    case 'submitted':
      return 'warning'
    case 'approved':
      return 'success'
    case 'rejected':
      return 'error'
    case 'reverted':
      return 'default'
    default:
      return 'default'
  }
}

const formatDate = (value) => {
  if (!value) return '-'
  try {
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return String(value)
  }
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const formatSyncTitle = (sync) =>
  sync?.name?.trim() ? sync.name.trim() : `Inventory Sync #${sync?.id ?? '-'}`

const InventorySyncApprovalsPage = () => {
  const navigate = useNavigate()

  const [syncs, setSyncs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [statusFilter, setStatusFilter] = useState('submitted')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const lastCommittedSearchRef = useRef('')

  const [selectedOrgIds, setSelectedOrgIds] = useState([])
  const {
    searchText: orgSearchText,
    setSearchText: setOrgSearchText,
    options: organizations,
    loading: orgsLoading,
    selectedOrgs,
    setSelectedFromOrgs,
    selectAllVisible,
    clearSelected: clearSelectedOrgs,
  } = useOrganizationMultiSearch({
    enabled: true,
    selectedIds: selectedOrgIds,
  })

  const fetchSyncs = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter,
      }
      const trimmed = debouncedSearch.trim()
      if (trimmed) params.search = trimmed
      if (selectedOrgIds.length > 0) {
        params.orgIds = selectedOrgIds.join(',')
      }
      const response = await inventorySyncAdminService.list(params)
      setSyncs(response.items || response.data || [])
      setTotalCount(response.pagination?.total || 0)
    } catch (err) {
      console.error('Error fetching inventory syncs:', err)
      setError(
        err.response?.data?.message || 'Failed to fetch inventory syncs',
      )
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, statusFilter, debouncedSearch, selectedOrgIds])

  useEffect(() => {
    fetchSyncs()
  }, [fetchSyncs])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === lastCommittedSearchRef.current) return
      lastCommittedSearchRef.current = searchInput
      setDebouncedSearch(searchInput)
      setPage(0)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const handleChangePage = (_event, newPage) => setPage(newPage)
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value)
    setPage(0)
  }
  const handleSearchChange = (event) => setSearchInput(event.target.value)
  const handleSearchSubmit = (event) => {
    event.preventDefault()
    const trimmed = searchInput.trim()
    setSearchInput(trimmed)
    lastCommittedSearchRef.current = trimmed
    setDebouncedSearch(trimmed)
    setPage(0)
  }
  const handleSearchClear = () => {
    setSearchInput('')
    lastCommittedSearchRef.current = ''
    setDebouncedSearch('')
    setPage(0)
  }
  const handleRowClick = (syncId) => {
    navigate(`/inventory-sync/${syncId}`)
  }

  if (loading && syncs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            Inventory Sync Approvals
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Review inventory sync requests from stores and admin-created syncs
            across all shops.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => navigate('/inventory-sync/create')}
          sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c40000' } }}
        >
          Create sync
        </Button>
      </Box>

      {/* Org filter */}
      <Card sx={{ mb: 3, p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Shop Id
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            loading={orgsLoading}
            options={organizations}
            filterOptions={(opts) => opts}
            inputValue={orgSearchText}
            onInputChange={(_, value, reason) => {
              if (reason === 'input' || reason === 'clear') {
                setOrgSearchText(value || '')
              }
            }}
            getOptionLabel={(o) => formatOrganizationLabel(o)}
            isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
            value={selectedOrgs}
            onChange={(_, newValue) => {
              setSelectedOrgIds(setSelectedFromOrgs(newValue))
              setPage(0)
            }}
            filterSelectedOptions={false}
            sx={{ flex: '1 1 320px', minWidth: 260, maxWidth: 560 }}
            renderOption={(props, option, { selected }) => {
              const { key, ...rest } = props
              return (
                <li key={key} {...rest}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {formatOrganizationLabel(option)}
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Shop Id"
                placeholder="Search shop name or Shop Id…"
                size="small"
                helperText="Type to search all shops by name or Shop Id."
              />
            )}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={!organizations.length}
            onClick={() => {
              setSelectedOrgIds(selectAllVisible())
              setPage(0)
            }}
            sx={{ color: '#e90000', borderColor: '#e90000', height: 40 }}
          >
            Select all
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSelectedOrgIds(clearSelectedOrgs())
              setPage(0)
            }}
            sx={{ height: 40 }}
          >
            Clear
          </Button>
        </Box>
        <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
          Leave empty to show syncs from every shop.
        </Typography>
      </Card>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 280px', maxWidth: 500 }}>
          <form onSubmit={handleSearchSubmit}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Search"
                value={searchInput}
                onChange={handleSearchChange}
                size="small"
                sx={{ flexGrow: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput && (
                    <InputAdornment position="end">
                      <Button
                        type="button"
                        size="small"
                        onClick={handleSearchClear}
                        sx={{ minWidth: 'auto', p: 0.5, color: '#666' }}
                      >
                        Clear
                      </Button>
                    </InputAdornment>
                  ),
                }}
                placeholder="Sync ID, name, or Shop Id..."
              />
              <Button
                type="submit"
                variant="contained"
                sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#1B5E20' }, minWidth: 'auto', px: 2 }}
              >
                Search
              </Button>
            </Box>
          </form>
        </Box>

        <FormControl sx={{ minWidth: 240 }} size="small">
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Filter by Status"
          >
            <MenuItem value="submitted">Submitted (awaiting approval)</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="reverted">Reverted</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Sync</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Shop Id</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Shop Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Initiated by</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }} align="right">Items</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {syncs.map((sync) => (
                <TableRow
                  key={sync.id}
                  onClick={() => handleRowClick(sync.id)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f8f9fb' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatSyncTitle(sync)}
                    </Typography>
                    {sync.name ? (
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        #{sync.id}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{sync.org_id ?? '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{sync.org_name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        sync.initiated_by === 'admin' ||
                        sync.meta?.initiated_by === 'admin'
                          ? 'Admin'
                          : 'Store'
                      }
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{sync.items_count ?? 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[sync.status] || sync.status}
                      color={getStatusChipColor(sync.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(sync.created_at)}</TableCell>
                  <TableCell>{formatDate(sync.updated_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {syncs.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
            <Typography>No inventory syncs found</Typography>
          </Box>
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </Box>
  )
}

export default InventorySyncApprovalsPage
